import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { BaseService } from '../../../../../services/base.service';
import { Subject, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { WidgetsService } from '../../../../../services/widgets.service';
import {
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  FUNNEL_TYPE,
  activeTranslations,
  WIDGET_INPUTS,
  WIDGET_TIME,
  WIDGET_TYPES,
  WIDGETS,
} from '../../../../../enum/constants';
import { IError, IFunnel } from '../../../../../interfaces';
import { CrmService } from '../../../../../services/crm.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { environment } from '../../../../../../environments/environment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AdditionalSettings, CodeForm, IconColor, InputForm } from './create-widget.interface';
import { ChannelService } from '../../../../../services/channel.service';

@Component({
  selector: 'app-create-widget',
  templateUrl: './create-widget.component.html',
  styleUrls: ['./create-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateWidgetComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<null>();
  public inputForm!: FormGroup<InputForm>;
  public iconColorForm!: FormGroup<IconColor>;
  public additionalSettingsForm!: FormGroup<AdditionalSettings>;
  public codeForm!: FormGroup<CodeForm>;
  public allowedFunnels!: IFunnel[];
  public widgetName!: string;

  time = [
    { name: WIDGET_TIME.THREE, value: '3' },
    { name: WIDGET_TIME.FIVE, value: '5' },
    { name: WIDGET_TIME.TEN, value: '10' },
    { name: WIDGET_TIME.FIFTEEN, value: '15' },
    { name: WIDGET_TIME.THIRTY, value: '30' },
  ];

  types = [
    { name: WIDGET_INPUTS.TEXT, value: 'text' },
    { name: WIDGET_INPUTS.COMMENT, value: 'textarea' },
  ];

  widget_types = [
    { name: WIDGETS.FORM, value: WIDGET_TYPES.FORM },
    { name: WIDGETS.CHAT, value: WIDGET_TYPES.CHAT },
  ];

  positions = [
    { icon: 'keyboard_arrow_up', value: 'top-left' },
    { icon: 'keyboard_arrow_up', value: 'top-center' },
    { icon: 'keyboard_arrow_up', value: 'top-right' },
    { icon: 'keyboard_arrow_down', value: 'bottom-left' },
    { icon: 'keyboard_arrow_down', value: 'bottom-center' },
    { icon: 'keyboard_arrow_down', value: 'bottom-right' },
  ];

  root = document.documentElement;
  primaryColor = getComputedStyle(this.root).getPropertyValue('--primary-color').trim();
  public showAddInput = false;
  public isEditInput = false;
  public inputs: any = [];
  public tempInputs: any = [];
  backgroundColor = this.primaryColor;
  iconColor: string = '#ffffff';
  selectedPosition: string = 'bottom-right';
  inputIndex!: number | null;
  private subdomain!: string;
  public channel!: any;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    public readonly baseService: BaseService,
    public readonly widgetsService: WidgetsService,
    private readonly crmService: CrmService,
    private readonly notificationService: NotificationService,
    private readonly channelService: ChannelService,
  ) {}

  public ngOnInit() {
    this.widgetName = this.payload?.channel?.name;
    this.channel = this.payload?.channel;
    if (this.channel.x_data) {
      this.inputs = this.payload?.channel.x_data?.inputs;
      this.iconColor = this.payload?.channel.x_data?.icon_color;
      this.backgroundColor = this.payload?.channel.x_data?.icon_background;
      this.selectedPosition = this.payload?.channel.x_data?.selected_position;
    }
    this.baseService.currentCompanyInfo$.subscribe((company: any) => {
      this.subdomain = company.subdomain;
    });

    this.initForm();
    this.onGetAllFunnels();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public initForm() {
    this.inputForm = new FormGroup<InputForm>({
      type: new FormControl('', Validators.required),
      placeholder: new FormControl(''),
      name: new FormControl('', Validators.required),
      is_required: new FormControl(false),
    });

    this.iconColorForm = new FormGroup<IconColor>({
      icon_color: new FormControl(this.iconColor),
      icon_background: new FormControl(this.backgroundColor),
    });

    this.additionalSettingsForm = new FormGroup<AdditionalSettings>({
      time: new FormControl(this.channel?.x_data?.time ?? 'now'),
      is_timeout: new FormControl(this.channel?.x_data?.is_timeout ?? false),
      is_mobile: new FormControl(this.channel?.x_data?.is_mobile ?? false),
      is_jecrm: new FormControl(this.channel?.x_data?.is_jecrm ?? true),
      widget_name: new FormControl(this.widgetName ?? '', Validators.required),
      widget_type: new FormControl(this.channel?.x_data?.widget_type ?? WIDGET_TYPES.FORM, Validators.required),
    });

    this.codeForm = new FormGroup<CodeForm>({
      code: new FormControl(this.channel?.x_data?.link ?? ''),
    });
  }

  public onNoClick(): void {
    this.close.emit('close');
  }

  public addInput() {
    this.showAddInput = true;
  }

  public saveInput() {
    this.inputForm.markAllAsTouched();
    if (this.inputForm.valid) {
      const input = this.inputForm.getRawValue();
      this.inputs.push(input);
      this.inputForm.reset();
      this.showAddInput = false;
    }
  }

  public deleteInput(index: number) {
    this.inputs.splice(index, 1);
  }

  setBackgroundColor(newColor: string) {
    this.backgroundColor = newColor;
  }

  setColor(newColor: string) {
    this.iconColor = newColor;
  }

  setPosition(position: string) {
    this.selectedPosition = position;
  }

  save() {
    if (
      this.additionalSettingsForm.valid &&
      this.iconColorForm.valid &&
      (this.inputs.length > 0 || this.additionalSettingsForm.controls.widget_type.value === WIDGET_TYPES.CHAT)
    ) {
      const additionalSettings = this.additionalSettingsForm.getRawValue();
      const iconColor = this.iconColorForm.getRawValue();
      const data = {
        channel_id: this.channel.id,
        inputs: this.inputs,
        selected_position: this.selectedPosition,
        ...additionalSettings,
        ...iconColor,
        baseURL: environment.baseURL,
        subdomain: this.subdomain,
      };
      this.widgetsService.addWidget(data).subscribe((res: any) => {
        const link = `<script src="${res.link}"></script>`;
        this.codeForm.controls.code.setValue(link);
        this.notificationService.showSuccess(
          '',
          activeTranslations.notifications_texts.success.widget_saved,
        );
        this.codeForm.updateValueAndValidity();
        this.channel.status = '';
        const channelData = {
          ...this.channel,
          name: data.widget_name,
          x_data: {
            link: link,
            key: res.key,
            ...data,
          },
        };
        this.channelService.update(this.channel.id, channelData).subscribe((res: any) => {});
      });
    }
    if (!this.inputs.length && this.additionalSettingsForm.controls.widget_type.value === WIDGET_TYPES.FORM) {
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.error_empty_form,
      );
    }
    this.additionalSettingsForm.markAllAsTouched();
  }

  onGetAllFunnels() {
    this.crmService
      .getAllFunnels({ type: FUNNEL_TYPE.DEAL })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (funnels: IFunnel[]) => {
          this.allowedFunnels = [...funnels];
        },
        error: (e) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  copyToClipboard() {
    const codeValue = this.codeForm.controls.code.value;
    if (codeValue) {
      navigator.clipboard
        .writeText(codeValue)
        .then(() => {
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success
              .the_code_is_copied_to_the_clipboard,
          );
        })
        .catch((err) => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.failed_to_copy_code,
          );
        });
    }
  }

  closeAddInput() {
    this.showAddInput = false;
    this.inputForm.reset();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.inputs, event.previousIndex, event.currentIndex);
    if (this.isEditInput) {
      this.editInput(event.currentIndex);
    }
  }

  disableTimeOut() {
    this.additionalSettingsForm.controls.time.setValue('now');
    this.additionalSettingsForm.controls.is_timeout.setValue(false);
  }

  editInput(index: number) {
    if (this.inputIndex === index) {
      this.showAddInput = !this.showAddInput;
    } else {
      this.showAddInput = true;
      this.isEditInput = true;
      this.inputForm.controls.name.setValue(this.inputs[index].name);
      this.inputForm.controls.type.setValue(this.inputs[index].type);
      this.inputForm.controls.placeholder.setValue(this.inputs[index].placeholder);
      this.inputForm.controls.is_required.setValue(this.inputs[index].is_required);
    }

    this.inputIndex = this.showAddInput ? index : null;

    if (!this.showAddInput) {
      this.isEditInput = false;
      this.inputForm.reset();
    }
  }

  saveEditInput() {
    this.inputForm.markAllAsTouched();
    if (this.inputForm.valid) {
      const input = this.inputForm.getRawValue();

      if (this.inputIndex !== null) {
        this.inputs[this.inputIndex] = input;
      }

      this.inputForm.reset();
      this.showAddInput = false;
      this.isEditInput = false;
      this.inputIndex = null;
    }
  }

  setWidgetType(event: any) {
    if (event.value === WIDGET_TYPES.FORM) {
      this.inputs = [...this.tempInputs];
    } else {
      this.tempInputs = [...this.inputs];
      this.inputs.splice(0, this.inputs.length);
      this.inputForm.reset();
    }
  }

  protected readonly WIDGET_TYPES = WIDGET_TYPES;
}
